import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DraggableSection } from '../components/DraggableSection';
import { CanvaTips } from '../components/CanvaTips';
import { useAuth } from '../context/AuthContext';
import { templatesApi } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Download, 
  FileText, 
  Palette,
  Type,
  Image,
  Sparkles,
  Lock,
  Eye,
  Plus,
  GripVertical,
  Layers,
  Upload,
  X,
  Loader2
} from 'lucide-react';

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [fields, setFields] = useState({});
  const [sections, setSections] = useState([]);
  const [brandSettings, setBrandSettings] = useState({
    logo_url: '',
    primary_color: '#2F5233',
    secondary_color: '#E07A5F',
    font_family: 'Helvetica',
    organization_name: user?.organization_name || '',
  });
  const [activeTab, setActiveTab] = useState('content');
  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const isPro = user?.subscription_plan === 'pro';

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'svg'].includes(ext)) {
      toast.error('Please upload a JPEG, PNG, or SVG file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(',')[1];
        const token = localStorage.getItem('pitchpal_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ image_data: base64, filename: file.name }),
        });
        if (res.ok) {
          const data = await res.json();
          setBrandSettings(prev => ({ ...prev, logo_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
          toast.success('Logo uploaded');
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to upload');
        }
      } catch { toast.error('Upload failed'); }
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await templatesApi.getById(id);
        setTemplate(response.data);
        
        // Initialize fields
        const initialFields = {};
        response.data.fields.forEach(field => {
          initialFields[field.key] = '';
        });
        setFields(initialFields);

        // Parse template content into sections
        const content = response.data.full_content;
        const sectionData = response.data.sections.map((title, index) => ({
          id: `section-${index}`,
          title,
          content: extractSectionContent(content, title, response.data.sections[index + 1]),
          isCustom: false
        }));
        setSections(sectionData);
      } catch (error) {
        console.error('Error fetching template:', error);
        toast.error('Template not found');
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, navigate]);

  // Extract content between section markers
  const extractSectionContent = (fullContent, currentSection, nextSection) => {
    // Simple extraction - in production you'd want more sophisticated parsing
    const lines = fullContent.split('\n');
    let inSection = false;
    let content = [];
    
    for (const line of lines) {
      if (line.includes(currentSection) || inSection) {
        inSection = true;
        if (nextSection && line.includes(nextSection)) {
          break;
        }
        content.push(line);
      }
    }
    
    return content.join('\n') || `[${currentSection} content]`;
  };

  // Generate preview content from sections
  const previewContent = useMemo(() => {
    let content = sections.map(s => {
      // Strip HTML tags for preview
      const textContent = s.content.replace(/<[^>]*>/g, '');
      return `${s.title.toUpperCase()}\n\n${textContent}`;
    }).join('\n\n---\n\n');

    // Replace field placeholders
    Object.entries(fields).forEach(([key, value]) => {
      if (value) {
        content = content.replace(new RegExp(`\\[${key}\\]`, 'g'), value);
      }
    });

    return content;
  }, [sections, fields]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setSections((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSectionUpdate = (updatedSection) => {
    setSections(prev => 
      prev.map(s => s.id === updatedSection.id ? updatedSection : s)
    );
  };

  const handleSectionDelete = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const handleAddSection = () => {
    const newSection = {
      id: `custom-${Date.now()}`,
      title: 'New Section',
      content: '',
      isCustom: true
    };
    setSections(prev => [...prev, newSection]);
  };

  const handleFieldChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerate = async (format) => {
    if (!user) {
      toast.error('Please log in to download templates');
      navigate('/login');
      return;
    }

    if (user.downloads_remaining === 0) {
      toast.error('You have reached your download limit. Please upgrade your plan.');
      navigate('/pricing');
      return;
    }

    setGenerating(true);
    try {
      // Build content from sections
      const fullContent = sections.map(s => {
        let content = s.content.replace(/<[^>]*>/g, ''); // Strip HTML
        return `${s.title.toUpperCase()}\n\n${content}`;
      }).join('\n\n');

      // Replace field placeholders
      let processedContent = fullContent;
      Object.entries(fields).forEach(([key, value]) => {
        if (value) {
          processedContent = processedContent.replace(new RegExp(`\\[${key}\\]`, 'g'), value);
        }
      });

      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          template_id: id,
          format,
          fields,
          custom_content: processedContent,
          brand_settings: isPro ? brandSettings : null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate template');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.title.replace(/\s+/g, '_').toLowerCase()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Template downloaded as ${format.toUpperCase()}!`);
      
      // Refresh user to update download count
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="w-8 h-8 spinner" />
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-alabaster" data-testid="template-editor-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(`/templates/${id}`)}
            className="flex items-center gap-2 text-stone-600 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to template
          </button>
          
          <div className="flex items-center gap-2">
            {user && (
              <Badge variant="outline" className="border-stone-200">
                {user.downloads_remaining === -1 
                  ? 'Unlimited downloads' 
                  : `${user.downloads_remaining} downloads left`}
              </Badge>
            )}
            {isPro && (
              <Badge className="bg-primary text-white">
                <Sparkles className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            )}
          </div>
        </div>

        <div className="mb-6">
          <Badge className="bg-primary-50 text-primary border-0 mb-2">
            {template.category}
          </Badge>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-primary-900">
            Customize: {template.title}
          </h1>
          <p className="text-stone-600 mt-1">{template.description}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Editor Panel - Takes 3 columns */}
          <div className="lg:col-span-3">
            <Card className="bg-white border-stone-200 shadow-card">
              <CardHeader className="border-b border-stone-100">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="bg-stone-100">
                    <TabsTrigger value="content" className="flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      Content
                    </TabsTrigger>
                    <TabsTrigger value="sections" className="flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Sections
                      {!isPro && <Lock className="w-3 h-3 ml-1" />}
                    </TabsTrigger>
                    <TabsTrigger value="branding" className="flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Branding
                      {!isPro && <Lock className="w-3 h-3 ml-1" />}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>

              <CardContent className="p-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  {/* Content Tab - Fill in fields */}
                  <TabsContent value="content" className="mt-0">
                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                      <p className="text-sm text-stone-500 mb-4">
                        Fill in your organization's details below. Changes appear in the live preview.
                      </p>
                      {template.fields.map((field) => (
                        <div key={field.key} className="space-y-2">
                          <Label htmlFor={field.key} className="text-stone-700 font-medium">
                            {field.label}
                          </Label>
                          {field.type === 'textarea' ? (
                            <textarea
                              id={field.key}
                              placeholder={field.placeholder}
                              value={fields[field.key] || ''}
                              onChange={(e) => handleFieldChange(field.key, e.target.value)}
                              className="w-full min-h-[100px] p-3 border border-stone-200 rounded-lg resize-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-sm"
                              data-testid={`field-${field.key}`}
                            />
                          ) : (
                            <Input
                              id={field.key}
                              type="text"
                              placeholder={field.placeholder}
                              value={fields[field.key] || ''}
                              onChange={(e) => handleFieldChange(field.key, e.target.value)}
                              className="bg-white border-stone-200 focus:border-primary-300"
                              data-testid={`field-${field.key}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Sections Tab - Drag & Drop, Add/Edit Sections (Pro Only) */}
                  <TabsContent value="sections" className="mt-0">
                    {isPro ? (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-sm text-stone-600">
                              Drag to reorder, edit content, or add new sections.
                            </p>
                            <p className="text-xs text-stone-400 flex items-center gap-1 mt-1">
                              <GripVertical className="w-3 h-3" />
                              Drag the handle to reorder sections
                            </p>
                          </div>
                          <Button
                            onClick={handleAddSection}
                            size="sm"
                            className="bg-primary hover:bg-primary-800 text-white"
                            data-testid="add-section-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Section
                          </Button>
                        </div>

                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={sections.map(s => s.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="max-h-[500px] overflow-y-auto pr-2">
                              {sections.map((section) => (
                                <DraggableSection
                                  key={section.id}
                                  id={section.id}
                                  section={section}
                                  onUpdate={handleSectionUpdate}
                                  onDelete={handleSectionDelete}
                                  isCustom={section.isCustom}
                                  isPro={isPro}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Lock className="w-8 h-8 text-stone-400" />
                        </div>
                        <h3 className="font-semibold text-stone-900 mb-2">Pro Feature</h3>
                        <p className="text-stone-600 mb-2">
                          Upgrade to Pro to:
                        </p>
                        <ul className="text-sm text-stone-500 mb-4 space-y-1">
                          <li>• Reorder sections with drag & drop</li>
                          <li>• Add custom sections</li>
                          <li>• Edit section content with rich text</li>
                          <li>• Delete sections you don't need</li>
                        </ul>
                        <Button
                          onClick={() => navigate('/pricing')}
                          className="bg-primary hover:bg-primary-800 text-white"
                        >
                          Upgrade to Pro - $15.99/mo
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  {/* Branding Tab (Pro Only) */}
                  <TabsContent value="branding" className="mt-0">
                    {isPro ? (
                      <div className="space-y-6">
                        <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <span className="font-medium text-primary-900">Pro Feature</span>
                          </div>
                          <p className="text-sm text-primary-700">
                            Add your organization's branding to create professional, on-brand documents.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-stone-700 font-medium flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            Logo
                          </Label>
                          {brandSettings.logo_url ? (
                            <div className="flex items-center gap-3">
                              <div className="w-14 h-14 rounded-lg border border-stone-200 overflow-hidden bg-white flex items-center justify-center">
                                <img src={brandSettings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                              </div>
                              <div className="flex gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
                                  {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />} Change
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => setBrandSettings(prev => ({ ...prev, logo_url: '' }))} className="text-red-500 hover:bg-red-50">
                                  <X className="w-3 h-3 mr-1" /> Remove
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => logoInputRef.current?.click()}
                              disabled={uploadingLogo}
                              className="w-full border-2 border-dashed border-stone-300 rounded-lg p-4 text-center hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
                              data-testid="brand-logo-upload"
                            >
                              {uploadingLogo ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-1" /> : <Upload className="w-6 h-6 text-stone-400 mx-auto mb-1" />}
                              <p className="text-xs font-medium text-stone-600">{uploadingLogo ? 'Uploading...' : 'Upload logo'}</p>
                              <p className="text-[10px] text-stone-400">JPEG, PNG, SVG</p>
                            </button>
                          )}
                          <input ref={logoInputRef} type="file" accept=".jpg,.jpeg,.png,.svg" onChange={handleLogoUpload} className="hidden" data-testid="brand-logo" />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-stone-700 font-medium">Primary Color</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={brandSettings.primary_color}
                                onChange={(e) => setBrandSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                                className="w-10 h-10 rounded cursor-pointer border border-stone-200"
                                data-testid="brand-primary-color"
                              />
                              <Input
                                value={brandSettings.primary_color}
                                onChange={(e) => setBrandSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                                className="bg-white border-stone-200 font-mono text-sm"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-stone-700 font-medium">Secondary Color</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={brandSettings.secondary_color}
                                onChange={(e) => setBrandSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                                className="w-10 h-10 rounded cursor-pointer border border-stone-200"
                                data-testid="brand-secondary-color"
                              />
                              <Input
                                value={brandSettings.secondary_color}
                                onChange={(e) => setBrandSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                                className="bg-white border-stone-200 font-mono text-sm"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-stone-700 font-medium">Font Family</Label>
                          <select
                            value={brandSettings.font_family}
                            onChange={(e) => setBrandSettings(prev => ({ ...prev, font_family: e.target.value }))}
                            className="w-full h-10 px-3 bg-white border border-stone-200 rounded-md focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                            data-testid="brand-font"
                          >
                            <option value="Helvetica">Helvetica (Clean & Modern)</option>
                            <option value="Times-Roman">Times Roman (Classic)</option>
                            <option value="Courier">Courier (Typewriter)</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Lock className="w-8 h-8 text-stone-400" />
                        </div>
                        <h3 className="font-semibold text-stone-900 mb-2">Pro Feature</h3>
                        <p className="text-stone-600 mb-2">
                          Upgrade to Pro to customize branding:
                        </p>
                        <ul className="text-sm text-stone-500 mb-4 space-y-1">
                          <li>• Add your organization logo</li>
                          <li>• Set custom brand colors</li>
                          <li>• Choose your font style</li>
                        </ul>
                        <Button
                          onClick={() => navigate('/pricing')}
                          className="bg-primary hover:bg-primary-800 text-white"
                        >
                          Upgrade to Pro - $15.99/mo
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Download Buttons */}
            <Card className="bg-white border-stone-200 shadow-card mt-6">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Download Your Template</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={() => handleGenerate('docx')}
                    disabled={generating}
                    className="bg-primary hover:bg-primary-800 text-white h-12"
                    data-testid="download-docx"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {generating ? 'Generating...' : 'Download DOCX'}
                  </Button>
                  <Button
                    onClick={() => handleGenerate('pdf')}
                    disabled={generating}
                    variant="outline"
                    className="border-primary-200 text-primary hover:bg-primary-50 h-12"
                    data-testid="download-pdf"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {generating ? 'Generating...' : 'Download PDF'}
                  </Button>
                </div>
                
                {!user && (
                  <p className="text-sm text-stone-500 mt-4 text-center">
                    Please <a href="/login" className="text-primary hover:underline">log in</a> to download templates
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Preview & Tips Panel - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Preview */}
            <Card className="bg-white border-stone-200 shadow-card">
              <CardHeader className="border-b border-stone-100">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Eye className="w-5 h-5 text-primary" />
                  Live Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div 
                  className="bg-stone-50 rounded-lg p-6 max-h-[400px] overflow-y-auto"
                  style={{
                    fontFamily: isPro ? brandSettings.font_family : 'inherit',
                  }}
                >
                  {isPro && brandSettings.logo_url && (
                    <div className="mb-4">
                      <img 
                        src={brandSettings.logo_url} 
                        alt="Logo" 
                        className="h-12 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  <pre 
                    className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700"
                    style={{ fontFamily: 'inherit' }}
                  >
                    {previewContent}
                  </pre>
                </div>
                <p className="text-xs text-stone-500 mt-4 text-center">
                  This is a preview. Download for the full formatted document.
                </p>
              </CardContent>
            </Card>

            {/* Canva Tips */}
            <CanvaTips />
          </div>
        </div>
      </div>
    </div>
  );
}
